import type { FC } from "react";
import React, { useRef } from "react";

import cn from "classnames";
import { motion } from "motion/react";

import { useResizeObservable } from "../../hooks/useResizeObservable";

import type { SkeletonProps } from "./types";

import classes from "./Skeleton.styl";

const SKELETON_TEST_ID = "skeleton-test-id";
const SKELETON_DISPLAY_NAME = "Skeleton";
const SKELETON_BORDER_MINIMUM_RADIUS = 20;
const SKELETON_BORDER_RADIUS_FORMULA_CONSTANT = 3;

const SKELETON_ANIMATION_PARAMS_INITIAL = {
    opacity: 1,
    scale: 0,
    x: 0,
    y: 0,
} as const;

const SKELETON_ANIMATION_PARAMS_ANIMATE = {
    opacity: 0,
    scale: 3,
} as const;

const SKELETON_ANIMATION_PARAMS_TRANSITION = {
    duration: 1.7,
    delay: 0.2,
    repeat: Infinity,
    repeatDelay: 0.2,
    ease: "easeOut",
} as const;

/**
 * Компонент для отображения состояния первичной загрузки для элементов. Систематизирует анимацию таких загрузок. Может гибко настраивать свои размеры и форму.
 *
 * <a href="https://www.figma.com/design/vTg3KAvmAXA9LigeyiqCWL/%F0%9F%92%A0-Base-Components?m=auto&node-id=14518-102595&t=chxGL86a2Kmw60o4-1">Спека</a>
 * @constructor
 */
export const Skeleton: FC<SkeletonProps> = ({
    view = "primary",
    shape = "squircle",
    height = "100%",
    width = "100%",
    maxHeight,
    minHeight,
    maxWidth,
    minWidth,
    customVarsStyles,
    customColorsStyles,
    "data-tsid": tsid = SKELETON_TEST_ID,
}) => {
    const skeletonRef = useRef<HTMLDivElement>(null);

    const dimensions = useResizeObservable({
        observableElementRef: skeletonRef,
    });

    const skeletonClassNames = cn(
        classes["skeleton"],
        classes[`__view-${view}`],
        customVarsStyles,
        customColorsStyles,
    );

    const skeletonContainerClassNames = cn(
        classes["skeleton__container"],
        classes[`__shape-${shape}`],
    );

    const skeletonStyles = {
        height,
        width,
        maxWidth,
        minWidth,
        maxHeight,
        minHeight,
    };

    const skeletonContainerStyles = {
        "--skeleton-squircle-border-radius": `${Math.min(
            SKELETON_BORDER_MINIMUM_RADIUS,
            Math.round(
                Math.min(dimensions.width, dimensions.height) /
                    SKELETON_BORDER_RADIUS_FORMULA_CONSTANT,
            ),
        )}px`,
    };

    return (
        <div
            data-tsid={tsid}
            data-uikit={SKELETON_DISPLAY_NAME}
            className={skeletonClassNames}
            style={skeletonStyles}
        >
            <div
                className={skeletonContainerClassNames}
                style={skeletonContainerStyles}
                ref={skeletonRef}
            >
                <div className={classes["skeleton__background"]} />
                <motion.div
                    className={classes["skeleton__overlay"]}
                    style={{
                        "--shimmer-dimension": `${Math.max(dimensions.width, dimensions.height)}px`,
                    }}
                    initial={SKELETON_ANIMATION_PARAMS_INITIAL}
                    animate={SKELETON_ANIMATION_PARAMS_ANIMATE}
                    transition={SKELETON_ANIMATION_PARAMS_TRANSITION}
                />
            </div>
        </div>
    );
};

Skeleton.displayName = SKELETON_DISPLAY_NAME;

export default Skeleton;
